<template>
  <v-dialog v-model="localValue" width="1050px">
    <v-card>
      <h1 class="section-title pt-6 px-8 pb-0">{{ configuration.metric }} da {{ category.name }}{{ unit.mocked ? '' : ` na ${unit.name}` }}</h1>
      <div class="d-flex justify-space-between align-start px-8">
        <div class="d-flex align-start">
          <div>
            <p class="ma-0 period-label">Período referência</p>
            <div v-if="!removeReferenceData" class="d-flex align-center">
              <p class="ma-0 revenue-label my-2">{{ configuration.metric }}: {{ values.reference | genericFormatter(formatter) }}</p>
              <mf-quick-actions
                v-if="referenceComputedQuickAction"
                class="pb-1 pl-1"
                :quick-action-group-id="referenceComputedQuickAction.id"
                :segmentation-group="referenceComputedQuickAction.segmentationGroup"
                :short-description="referenceComputedQuickAction.shortDescription"
                :long-description="referenceComputedQuickAction.longDescription"
              />
            </div>
            <p v-else class="my-2">-</p>
            <v-chip> {{ formattedReferenceDate }} </v-chip>
          </div>
          <div class="ml-5">
            <p class="ma-0 period-label">Período atual</p>
            <div class="d-flex align-center">
              <p class="ma-0 revenue-label my-2">{{ configuration.metric }}: {{ values.current | genericFormatter(formatter) }}</p>
              <mf-quick-actions
                v-if="currentComputedQuickAction"
                class="pb-1 pl-1"
                :quick-action-group-id="currentComputedQuickAction.id"
                :segmentation-group="currentComputedQuickAction.segmentationGroup"
                :short-description="currentComputedQuickAction.shortDescription"
                :long-description="currentComputedQuickAction.longDescription"
              />
            </div>
            <v-chip> {{ formattedCurrentDate }} </v-chip>
          </div>
          <div class="ml-5">
            <p class="ma-0 mb-2 period-label">Variação</p>
            <div class="difference d-flex align-center mt-0 mb-2" :class="getVariatClass(values)">
              <div class="arrow-box d-flex justify-center align-center mx-1">
                <mf-icon size="12" color="branco" v-bind="getTooltipIcon((values.changePercentage * 100 || 0).toFixed(2))" />
              </div>

              <span class="my-0">{{ (values.changePercentage * 100) | localeString(2) }}%</span>
            </div>
          </div>
        </div>
        <div class="mf-search-container">
          <mf-search v-model.lazy="search" dense hide-details placeholder="Buscar produto" autofocus />
        </div>
      </div>
      <v-card-text>
        <v-row align="center">
          <v-col align="center">
            <paginated-data-table
              ref="table"
              query-name="productComparisonsMetrics"
              :headers="headers"
              :search="search"
              :query="query"
              :custom-items-per-page="5"
              :queryVariables="queryVariables"
              :graphql-uri="$microservicesUrls['campaigns']"
              default-table-sort-by="change_percentage"
              :default-table-sort-desc="true"
            >
              <template v-slot:item.product.name="{ item }">
                <a class="link" target="_blank" :href="`#/produtos/detalhes-produto/${item.product.sku}`">{{ item.product.name }}</a>
              </template>
              <template v-slot:item.reference_value="{ item }">
                {{ item.reference_value | genericFormatter(formatter) }}
              </template>
              <template v-slot:item.current_value="{ item }">
                {{ item.current_value | genericFormatter(formatter) }}
              </template>
              <template v-slot:item.change_percentage="{ item }">
                {{ item.change_percentage | localeString(2) | percent }}
              </template>
            </paginated-data-table>
          </v-col>
        </v-row>
      </v-card-text>

      <v-card-actions>
        <v-spacer />
        <mf-button label="Fechar" @click="localValue = false" />
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { QUERY_CATEGORY_PRODUCT_COMPARISON_METRICS } from '@/modules/produtos/comparativo-categorias/graphql'
import { mapState } from 'vuex'
import isEmpty from 'lodash/isEmpty'
import { DateTime } from 'luxon'
export default {
  name: 'CellInformationModal',
  components: {
    PaginatedDataTable: () => import('@/components/PaginatedDataTable'),
  },
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    configuration: {
      type: Object,
      default: () => ({}),
    },
    unit: {
      type: Object,
      default: () => ({}),
    },
    category: {
      type: Object,
      default: () => ({}),
    },
    referenceDate: {
      type: Array,
      default: () => [],
    },
    currentDate: {
      type: Array,
      default: () => [],
    },
    values: {
      type: Object,
      default: () => ({}),
    },
    formatter: {
      type: Object,
      default: () => ({}),
    },
    quickAction: {
      type: Object,
    },
    removeReferenceData: {
      type: Boolean,
      default: false,
    },
    isPurchasedOnReference: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    lazyValue: false,
    search: '',
    exporting: false,
  }),

  computed: {
    ...mapState({
      stateFilters: (state) => state.filters,
    }),
    referenceComputedQuickAction() {
      return this.computeQuickActionConfiguration('reference')
    },
    currentComputedQuickAction() {
      return this.computeQuickActionConfiguration('current')
    },
    unitConfiguration() {
      if (!isEmpty(this.unit)) {
        if (this.unit.mocked) return undefined
        else return [this.stateFilters.unitsConfiguration.find((unit) => unit.value === this.unit.id_erp)]
      }
      return undefined
    },
    localValue: {
      get() {
        return this.lazyValue
      },
      set(value) {
        this.lazyValue = value
        this.$emit('input', value)
      },
    },
    formattedReferenceDate() {
      const initialDate = this.referenceDate[0]
      const finalDate = this.referenceDate[1]
      return DateTime.fromFormat(initialDate, 'yyyy-MM-dd').toFormat('dd/MM/yyyy') + ' - ' + DateTime.fromFormat(finalDate, 'yyyy-MM-dd').toFormat('dd/MM/yyyy')
    },
    formattedCurrentDate() {
      const initialDate = this.currentDate[0]
      const finalDate = this.currentDate[1]
      return DateTime.fromFormat(initialDate, 'yyyy-MM-dd').toFormat('dd/MM/yyyy') + ' - ' + DateTime.fromFormat(finalDate, 'yyyy-MM-dd').toFormat('dd/MM/yyyy')
    },

    /* Products table */
    headers() {
      if (this.configuration.customHeaders) {
        return this.configuration.customHeaders
      }
      return [
        {
          text: 'Produto',
          value: 'product.name',
          width: '15%',
          sortable: false,
        },
        {
          text: `${this.configuration.metric} - Referência`,
          value: 'reference_value',
          width: '20%',
        },
        {
          text: `${this.configuration.metric} - Atual`,
          value: 'current_value',
          width: '20%',
        },
        {
          text: `Alteração Percentual`,
          value: 'change_percentage',
          width: '20%',
        },
      ]
    },
    queryVariables() {
      return {
        metricName: this.configuration.metricKey,
        categories: [this.category.id],
        units: this.unitConfiguration,
        referenceInitialDate: this.referenceDate[0],
        referenceFinalDate: this.referenceDate[1],
        currentInitialDate: this.currentDate[0],
        currentFinalDate: this.currentDate[1],
      }
    },
    query() {
      return QUERY_CATEGORY_PRODUCT_COMPARISON_METRICS
    },
  },

  watch: {
    value(value) {
      this.lazyValue = value
    },
  },

  mounted() {
    this.lazyValue = this.value
  },

  methods: {
    getVariatClass(value) {
      const newValue = (value.changePercentage * 100 || 0).toFixed(2)
      if (this.isPurchasedOnReference) return newValue === '0.00' ? '' : 'difference-negative'
      else if (newValue > 0 && !this.isPurchasedOnReference) return 'difference-positive'
      else if (newValue < 0) return 'difference-negative'
      return ''
    },
    getTooltipIcon(percentage) {
      if (percentage === '0.00') {
        return { icon: 'remove' }
      } else if (percentage < 0 || this.isPurchasedOnReference) {
        return { icon: 'arrow_downward' }
      } else {
        return { icon: 'arrow_upward' }
      }
    },
    computeQuickActionConfiguration(periodId) {
      if (!this.quickAction?.[periodId]) return null
      const fmtReferenceDates = this.referenceDate.map((date) => date.replaceAll('-', ''))
      const fmtCurrentDates = this.currentDate.map((date) => date.replaceAll('-', ''))
      const unitIds = this.unit.mocked ? [] : [this.unit.id_erp]
      return {
        id: `${this.category.id}_${this.unit.id_erp}_${this.configuration.metric}_${periodId}`,
        shortDescription: this.quickAction[periodId].shortDescription(this.category),
        longDescription: this.quickAction[periodId].longDescription(this.category),
        segmentationGroup: this.quickAction[periodId].segmentationGroup(this.category, fmtReferenceDates, fmtCurrentDates, unitIds),
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.section-title {
  font-size: 26px;
  font-weight: bold;
  color: $cinza-d20;
}
.period-label {
  font-size: 16px;
  color: $cinza-d10;
  line-height: 120%;
}

.revenue-label {
  font-size: 14px;
  color: $marca-1;
  line-height: 120%;
}

.mf-search-container {
  width: 240px;
}

.difference {
  font-size: 16px;
  color: $cinza-0;
  font-weight: 600;
}

.difference {
  span {
    font-size: 16px;
    font-weight: 600;
    color: $cinza-0;
  }
}

.arrow-box {
  width: 20px;
  height: 20px;
  border-radius: 4px;
  background: $cinza-0;
}

.difference-negative {
  span {
    color: $vermelho-0;
  }

  .arrow-box {
    background-color: $vermelho-0;
  }
}

.difference-positive {
  span {
    color: $verde-0;
  }

  .arrow-box {
    background-color: $verde-0;
  }
}
</style>
